'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { FaWhatsapp } from 'react-icons/fa';
import { useLanguage } from '@/contexts/LanguageContext';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false)
  const { language, setLanguage, t, dir } = useLanguage()
  const router = useRouter()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { id: 'nav_about',         label: t('nav.about'),         href: '/homepage#about' },
    { id: 'nav_catalog',       label: t('nav.catalog'),       href: '/homepage#catalog' },
    { id: 'nav_quality',       label: t('nav.quality'),       href: '/homepage#quality' },
    { id: 'nav_logistics',     label: t('nav.logistics'),     href: '/homepage#logistics' },
    { id: 'nav_values',        label: t('nav.values'),        href: '/homepage#values' },
    { id: 'nav_certifications',label: t('nav.certifications'),href: '/homepage#certifications' },
    // { id: 'nav_contact',       label: t('nav.contact_nav'),   href: '/homepage#contact' },
  ]

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ]

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-custom shadow-md py-2'
          : 'bg-transparent py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-2 md:px-4">
        <div className="flex items-center justify-between w-full">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group flex-shrink-0 mr-8">
            <AppImage
              src="/assets/logo/LOGO.png"
              alt="AutofitSeafood logo - Mauritanian seafood export company"
              width={100}
              height={100}
              className="w-32 h-32 object-contain bg-transparent"
            />
            {/* <div className="flex flex-col leading-tight">
              <span
                className={`font-serif text-base font-bold tracking-wide transition-colors whitespace-nowrap ${
                isScrolled ? 'text-foreground' : 'text-black'
                }`}
              >
                AUTOFIT CANADA LTD
              </span>
              <span className="text-[10px] font-medium tracking-widest uppercase text-primary/80">
              </span>
            </div> */}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={link.href}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-primary ${
                  isScrolled ? 'text-foreground' : 'text-black'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Buttons + Language Switcher */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isScrolled
                    ? 'text-foreground hover:bg-muted'
                    : 'text-black hover:bg-white/10'
                }`}
              >
                <span className="text-xl">
                  {languages.find(l => l.code === language)?.flag}
                </span>
                <Icon name="ChevronDownIcon" size={16} />
              </button>

              {isLangMenuOpen && (
                <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-ocean-lg border border-border py-2 min-w-[160px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code as any)
                        setIsLangMenuOpen(false)
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-muted transition-colors ${
                        language === lang.code ? 'bg-primary/10 text-primary' : 'text-foreground'
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm font-medium">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://wa.me/14506011665"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isScrolled
                  ? 'text-foreground hover:bg-muted'
                  : 'text-black hover:bg-white/10'
              }`}
            >
              {/* <FaWhatsapp size={20} className="text-green-500" /> */}
              {/* <span className="text-sm font-medium">{t('nav.whatsapp')}</span> */}
            </a>
            <button className="btn-primary" onClick={() => router.push('/homepage#contact')}>{t('nav.contact')}</button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled
                ? 'text-foreground hover:bg-muted'
                : 'text-black hover:bg-white/10'
            }`}
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-6 pb-6 border-t border-border pt-6 space-y-4 bg-white/95 backdrop-blur-custom rounded-lg px-4 -mx-4">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-foreground hover:text-primary font-medium py-2 transition-colors"
              >
                {link.label}
              </a>
            ))}

            {/* Mobile Language Switcher */}
            <div className="pt-4 border-t border-border">
              <div className="text-sm font-semibold text-muted-foreground mb-3">Language</div>
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code as any)
                      setIsMobileMenuOpen(false)
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      language === lang.code
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-foreground border-border hover:border-primary'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="text-xs font-medium">{lang.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-4 border-t border-border">
              <a
                href="https://wa.me/14506011665"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-primary text-primary hover:bg-primary hover:text-white transition-colors"
              >
                <FaWhatsapp size={20} />
                <span className="font-medium">{t('nav.whatsapp')}</span>
              </a>
              <button className="btn-primary w-full" onClick={() => router.push('/contact')}>{t('nav.contact')}</button>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}